import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdAdd, MdDelete, MdPictureAsPdf, MdShare, MdClose } from 'react-icons/md';
import jsPDF from 'jspdf';
import { ROUTES } from '../constants/endpoints';
import { customerAPI } from '../services/adminAPI';
import logo from '../assets/logo.jpeg';
import '../styles/Quotation.css';

const emptyRow = () => ({ itemCode: '', description: '', hsnSac: '', quantity: '', rate: '', unit: '' });

const defaultIssuer = {
  companyName: 'KUSCO PVT LTD',
  gstNo: '10AAMCK5128N1ZD',
  panNo: 'AAMCK5128N',
  hsnCode: 'ABC',
  msmeNo: '123',
  address: 'Kusha, Narhat, Nawada',
  phone: '8252745476',
  email: 'info@kusco.in',
  website: 'www.kusco.in',
  state: 'Bihar',
  stateCode: '805122',
};

const numberFormat = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const parseNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) => numberFormat.format(value || 0);

const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const convertToWords = (n) => {
    if (n === 0) return '';
    let str = '';
    if (Math.floor(n / 10000000) > 0) {
      str += convertToWords(Math.floor(n / 10000000)) + ' Crore ';
      n %= 10000000;
    }
    if (Math.floor(n / 100000) > 0) {
      str += convertToWords(Math.floor(n / 100000)) + ' Lakh ';
      n %= 100000;
    }
    if (Math.floor(n / 1000) > 0) {
      str += convertToWords(Math.floor(n / 1000)) + ' Thousand ';
      n %= 1000;
    }
    if (Math.floor(n / 100) > 0) {
      str += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n > 0) {
      if (n < 10) str += ones[n];
      else if (n < 20) str += teens[n - 10];
      else {
        str += tens[Math.floor(n / 10)];
        if (n % 10 > 0) str += ' ' + ones[n % 10];
      }
    }
    // Replace multiple spaces with single space
    return str.replace(/\s+/g, ' ').trim();
  };
  
  const number = Math.abs(num);
  const rupees = Math.floor(number);
  const paise = Math.round((number - rupees) * 100);
  
  let result = '';
  if (rupees > 0) result += convertToWords(rupees) + ' Rupees';
  if (paise > 0) {
    if (rupees > 0) result += ' and ';
    result += convertToWords(paise) + ' Paise';
  }
  if (rupees === 0 && paise === 0) result = 'Zero Rupees';
  // Replace any multiple spaces in final result
  return result.replace(/\s+/g, ' ').trim() + ' Only';
};

const buildInitialDraft = (inq) => ({
  issuer: { ...defaultIssuer },
  documentMeta: {
    customerName: inq?.company || inq?.name || '',
    customerAttention: inq?.name || '',
    customerAddress: inq?.address || '',
    customerEmail: inq?.email || '',
    customerPhone: inq?.phone || '',
    customerGstNo: '',
    quotationNo: '',
    invoiceNo: '',
    taxInvoiceNo: '',
    challanNo: '',
    inquiryNo: inq?.inquiryNo || '',
    inquiryDate: inq?.createdAt ? new Date(inq.createdAt).toISOString().slice(0, 10) : '',
    inquiryDueDate: '',
    documentDate: new Date().toISOString().slice(0, 10),
    poNumber: '',
    poDate: '',
    challanNumber: '',
    challanDate: '',
    destination: '',
    dispatchThrough: '',
  },
  rows: [emptyRow()],
  terms: {
    tax: '',
    payment: '',
    deliverySchedule: '',
    packingForwarding: '',
    packingForwardingPercent: '',
    freightAmount: '',
    packingForwardingAmount: '',
    freightCharge: '',
    discountPercent: '',
    validity: '',
    notes: '',
  },
});

const DEPARTMENTS = [
  'Planning Dept',
  'Design Dept',
  'Purchase Dept',
  'Sales Dept',
  'Sales Coordinator',
  'Account Dept',
  'Production Dept',
  'Service Dept',
  'Store Dept',
];

const loadDraft = (key, inq) => {
  if (!key) return buildInitialDraft(inq);

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return buildInitialDraft(inq);

    const parsed = JSON.parse(raw);
    const fallback = buildInitialDraft(inq);

    // Migrate old fields
    const migratedMeta = { ...fallback.documentMeta, ...(parsed.documentMeta || {}) };
    if (parsed.documentMeta?.chalanNo) migratedMeta.challanNo = parsed.documentMeta.chalanNo;
    if (parsed.documentMeta?.chalanNumber) migratedMeta.challanNumber = parsed.documentMeta.chalanNumber;
    if (parsed.documentMeta?.chalanDate) migratedMeta.challanDate = parsed.documentMeta.chalanDate;

    return {
      issuer: { ...fallback.issuer, ...(parsed.issuer || {}) },
      documentMeta: migratedMeta,
      rows: Array.isArray(parsed.rows) && parsed.rows.length ? parsed.rows : fallback.rows,
      terms: { ...fallback.terms, ...(parsed.terms || {}) },
    };
  } catch {
    return buildInitialDraft(inq);
  }
};

const buildAutoDocumentNumber = (numberField, inq) => {
  const inquiryPart = inq?.inquiryNo || inq?._id || 'DRAFT';
  if (numberField === 'quotationNo') return `QT-${inquiryPart}`;
  if (numberField === 'invoiceNo') return `PI-${inquiryPart}`;
  if (numberField === 'taxInvoiceNo') return `TI-${inquiryPart}`;
  if (numberField === 'challanNo') return `CH-${inquiryPart}`;
  return inquiryPart;
};

const loadImageAsDataURL = async (src) => {
  const response = await fetch(src);
  const blob = await response.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const drawWrappedText = (pdf, text, x, y, maxWidth, lineHeight = 4.6, options = {}) => {
  const safeText = text ? String(text) : '';
  const lines = safeText ? pdf.splitTextToSize(safeText, maxWidth) : [''];
  pdf.text(lines, x, y, options);
  return lines.length * lineHeight;
};

const DocumentComposer = ({
  pageTitle,
  documentTitle,
  numberLabel,
  numberField,
  numberPlaceholder,
  filePrefix,
  accentColor,
  titleBarColor,
  pdfButtonColor,
  footerNote,
  totalLabel = 'Grand Total',
  taxLabel = 'GST',
  includeFreightInTotal = false,
  showFreightAmountField = false,
  showTaxAmountLine = true,
  includePackingForwardingInTotal = false,
  moveTaxBelowPacking = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inq = location.state?.inq;
  const draftBase = inq?._id || inq?.inquiryNo || 'default';
  const draftKey = `${draftBase}:${numberField || 'default'}`;
  const isExtendedDetails = documentTitle === 'TAX INVOICE' || numberField === 'taxInvoiceNo' || documentTitle === 'CHALLAN' || numberField === 'challanNo';
  const isOnlyChalan = documentTitle === 'CHALLAN' || numberField === 'challanNo';

  const initialDraft = loadDraft(draftKey, inq);

  const [issuer, setIssuer] = useState(initialDraft.issuer);
  const [documentMeta, setDocumentMeta] = useState(initialDraft.documentMeta);
  const [rows, setRows] = useState(initialDraft.rows);
  const [terms, setTerms] = useState(initialDraft.terms);
  const [generating, setGenerating] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareDept, setShareDept] = useState('');
  const [shareComment, setShareComment] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');
  const documentNumber = documentMeta[numberField];

  useEffect(() => {
    if (!numberField) return;
    if (documentNumber) return;
    setDocumentMeta((prev) => ({
      ...prev,
      [numberField]: buildAutoDocumentNumber(numberField, inq),
    }));
  }, [documentNumber, inq, numberField]);

  const handleIssuerChange = (e) => {
    setIssuer((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleMetaChange = (e) => {
    setDocumentMeta((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleTermsChange = (e) => {
    setTerms((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleRowChange = (index, e) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [e.target.name]: e.target.value } : row
      )
    );
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (index) => setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));

  useEffect(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify({ issuer, documentMeta, rows, terms }));
    } catch {
      // Ignore storage failures and keep the editor usable.
    }
  }, [draftKey, issuer, documentMeta, rows, terms]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== draftKey || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed.issuer) setIssuer((prev) => ({ ...prev, ...parsed.issuer }));
        if (parsed.documentMeta) setDocumentMeta((prev) => ({ ...prev, ...parsed.documentMeta }));
        if (Array.isArray(parsed.rows) && parsed.rows.length) setRows(parsed.rows);
        if (parsed.terms) setTerms((prev) => ({ ...prev, ...parsed.terms }));
      } catch {
        // Ignore malformed external updates.
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [draftKey]);

  useEffect(() => {
    if (!shareSuccess && !shareError) return undefined;

    const timer = setTimeout(() => {
      setShareSuccess('');
      setShareError('');
    }, 4000);

    return () => clearTimeout(timer);
  }, [shareSuccess, shareError]);

  const subtotal = rows.reduce(
    (sum, row) => sum + parseNumber(row.quantity) * parseNumber(row.rate),
    0
  );
  const freightAmount = includeFreightInTotal ? parseNumber(terms.freightAmount) : 0;
  const packingForwardingPercent = includePackingForwardingInTotal ? parseNumber(terms.packingForwardingPercent) : 0;
  const packingForwardingAmount = subtotal * (packingForwardingPercent / 100);
  const taxRate = parseNumber(terms.tax);
  const taxPackingForwardingCharge = isExtendedDetails ? parseNumber(terms.packingForwardingAmount) : packingForwardingAmount;
  const taxFreightCharge = isExtendedDetails ? parseNumber(terms.freightCharge) : freightAmount;
  const taxDiscountPercent = isExtendedDetails ? parseNumber(terms.discountPercent) : 0;
  const taxDiscountAmount = isExtendedDetails
    ? (subtotal + taxPackingForwardingCharge + taxFreightCharge) * (taxDiscountPercent / 100)
    : 0;
  const mainPrice = isExtendedDetails
    ? Math.max(0, subtotal + taxPackingForwardingCharge + taxFreightCharge - taxDiscountAmount)
    : subtotal + freightAmount + packingForwardingAmount;
  const taxableBase = isExtendedDetails ? mainPrice : subtotal + freightAmount + packingForwardingAmount;
  const taxAmount = taxableBase * (taxRate / 100);
  const grandTotal = taxableBase + taxAmount;

  const buildPdfDocument = async () => {
    setGenerating(true);
    try {
      const logoData = await loadImageAsDataURL(logo);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = 210;
      const marginX = 10;
      const contentW = pageW - marginX * 2;
      const accent = accentColor || '#667eea';
      const dark = '#111827';
      const muted = '#475569';
      const light = '#f8fafc';
      const isExtendedDetails = documentTitle === 'TAX INVOICE' || numberField === 'taxInvoiceNo' || documentTitle === 'CHALLAN' || numberField === 'challanNo';
      const isOnlyChalan = documentTitle === 'CHALLAN' || numberField === 'challanNo';

      const drawHeader = () => {
        pdf.setDrawColor(226, 232, 240);
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(marginX, 10, contentW, 34, 2, 2, 'S');

        pdf.addImage(logoData, 'JPEG', marginX + 3, 13, 24, 24);

        pdf.setTextColor(dark);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(17);
        pdf.text(issuer.companyName || 'KUSCO PVT LTD', marginX + 30, 18);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(muted);
        pdf.text(`GST No: ${issuer.gstNo || '________________'}`, marginX + 30, 24);
        pdf.text(`PAN No: ${issuer.panNo || '________________'}`, marginX + 85, 24);
        pdf.text(`HSN Code: ${issuer.hsnCode || '________________'}`, marginX + 30, 29);
        pdf.text(`MSME No: ${issuer.msmeNo || '________________'}`, marginX + 85, 29);
        pdf.text(
          issuer.address || 'Address line',
          marginX + 30,
          34,
          { maxWidth: 98 }
        );
        pdf.text(
          [issuer.phone ? `Ph: ${issuer.phone}` : '', issuer.email || '', issuer.website || '']
            .filter(Boolean)
            .join('  |  '),
          marginX + 30,
          39
        );

        const boxX = 150;
        const boxW = 50;
        pdf.setFillColor(245, 247, 250);
        pdf.setDrawColor(203, 213, 225);
        pdf.roundedRect(boxX, 12, boxW, 28, 2, 2, 'FD');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(15);
        pdf.setTextColor(dark);
        pdf.text(documentTitle, boxX + boxW / 2, 19, { align: 'center' });
        pdf.setDrawColor(accent);
        pdf.line(boxX + 4, 22, boxX + boxW - 4, 22);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(muted);
        pdf.text(`${numberLabel}: ${documentMeta[numberField] || 'Draft'}`, boxX + 3, 28);
        pdf.text(`Doc Date: ${documentMeta.documentDate}`, boxX + 3, 32);
      };

      // Calculate party section height dynamically
      const calculatePartySectionHeight = () => {
        const leftW = 122;
        const rightW = contentW - leftW - 4;
        const topPadding = 12;
        const bottomPadding = 4;
        const headerHeight = 8;
        const lineHeight = 4.2;
        
        const billLines = [
          `Company: ${documentMeta.customerName || '________________'}`,
          `Attention: ${documentMeta.customerAttention || '________________'}`,
          `Address: ${documentMeta.customerAddress || '________________'}`,
          `Email: ${documentMeta.customerEmail || '________________'}`,
          `Phone: ${documentMeta.customerPhone || '________________'}`,
          `GST No: ${documentMeta.customerGstNo || '________________'}`,
        ];
        
        let detailLines;
        if (isOnlyChalan) {
          // For Challan: only PO No, PO Date, Destination, Dispatch Through
          detailLines = [
            `PO No: ${documentMeta.poNumber || '________________'}`,
            `PO Date: ${documentMeta.poDate || '________________'}`,
            `Destination: ${documentMeta.destination || '________________'}`,
            `Dispatch Through: ${documentMeta.dispatchThrough || '________________'}`,
          ];
        } else if (isExtendedDetails) {
          // For Tax Invoice: PO, Challan, Destination, Dispatch Through
          detailLines = [
            `PO No: ${documentMeta.poNumber || '________________'}`,
            `PO Date: ${documentMeta.poDate || '________________'}`,
            `Challan No: ${documentMeta.challanNumber || '________________'}`,
            `Challan Date: ${documentMeta.challanDate || '________________'}`,
            `Destination: ${documentMeta.destination || '________________'}`,
            `Dispatch Through: ${documentMeta.dispatchThrough || '________________'}`,
          ];
        } else {
          // For others: standard lines
          detailLines = [
            `${numberLabel}: ${documentMeta[numberField] || 'Draft'}`,
            `Inquiry No: ${documentMeta.inquiryNo || '________________'}`,
            `Inquiry Date: ${documentMeta.inquiryDate || '________________'}`,
            `Due Date: ${documentMeta.inquiryDueDate || '________________'}`,
          ];
        }
        
        let billHeight = 0;
        billLines.forEach(() => billHeight += lineHeight);
        
        let detailHeight = 0;
        detailLines.forEach(() => detailHeight += lineHeight);
        
        const totalHeight = headerHeight + topPadding + Math.max(billHeight, detailHeight) + bottomPadding;
        return totalHeight;
      };
      
      const partySectionH = calculatePartySectionHeight();

      const drawPartySection = (y) => {
        const leftW = 122;
        const rightW = contentW - leftW - 4;

        pdf.setDrawColor(203, 213, 225);
        pdf.setFillColor(255, 255, 255);

        pdf.roundedRect(marginX, y, leftW, partySectionH, 2, 2, 'S');
        pdf.roundedRect(marginX + leftW + 4, y, rightW, partySectionH, 2, 2, 'S');

        pdf.setFillColor(accent);
        pdf.roundedRect(marginX, y, leftW, 8, 2, 2, 'F');
        pdf.roundedRect(marginX + leftW + 4, y, rightW, 8, 2, 2, 'F');

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.text('BILL TO', marginX + 3, y + 5.5);
        pdf.text('DOCUMENT DETAILS', marginX + leftW + 7, y + 5.5);

        pdf.setTextColor(dark);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.6);

        const billLines = [
          `Company: ${documentMeta.customerName || '________________'}`,
          `Attention: ${documentMeta.customerAttention || '________________'}`,
          `Address: ${documentMeta.customerAddress || '________________'}`,
          `Email: ${documentMeta.customerEmail || '________________'}`,
          `Phone: ${documentMeta.customerPhone || '________________'}`,
          `GST No: ${documentMeta.customerGstNo || '________________'}`,
        ];

        let billY = y + 12;
        billLines.forEach((line) => {
          billY += drawWrappedText(pdf, line, marginX + 3, billY, leftW - 6, 4.2);
        });

        let detailLines;
        if (isOnlyChalan) {
          detailLines = [
            `PO No: ${documentMeta.poNumber || '________________'}`,
            `PO Date: ${documentMeta.poDate || '________________'}`,
            `Destination: ${documentMeta.destination || '________________'}`,
            `Dispatch Through: ${documentMeta.dispatchThrough || '________________'}`,
          ];
        } else if (isExtendedDetails) {
          detailLines = [
            `PO No: ${documentMeta.poNumber || '________________'}`,
            `PO Date: ${documentMeta.poDate || '________________'}`,
            `Challan No: ${documentMeta.challanNumber || '________________'}`,
            `Challan Date: ${documentMeta.challanDate || '________________'}`,
            `Destination: ${documentMeta.destination || '________________'}`,
            `Dispatch Through: ${documentMeta.dispatchThrough || '________________'}`,
          ];
        } else {
          detailLines = [
            `${numberLabel}: ${documentMeta[numberField] || 'Draft'}`,
            `Inquiry No: ${documentMeta.inquiryNo || '________________'}`,
            `Inquiry Date: ${documentMeta.inquiryDate || '________________'}`,
            `Due Date: ${documentMeta.inquiryDueDate || '________________'}`,
          ];
        }

        let detailY = y + 12;
        detailLines.forEach((line) => {
          detailY += drawWrappedText(pdf, line, marginX + leftW + 7, detailY, rightW - 6, 4.2);
        });
      };

      const drawTableHeader = (y) => {
        const isOnlyTaxInvoice = documentTitle === 'TAX INVOICE' || numberField === 'taxInvoiceNo';
        let columns;
        if (isOnlyChalan) {
          // Challan: only Sr No, Item Code, Description, HSN/SAC (if extended), Qty
          columns = isExtendedDetails
            ? [
                { label: 'Sr No', width: 12 },
                { label: 'Item Code', width: 22 },
                { label: 'Description', width: 90 },
                { label: 'HSN/SAC', width: 20 },
                { label: 'Qty', width: 24 },
              ]
            : [
                { label: 'Sr No', width: 12 },
                { label: 'Item Code', width: 22 },
                { label: 'Description', width: 110 },
                { label: 'Qty', width: 24 },
              ];
        } else if (isOnlyTaxInvoice) {
          columns = [
            { label: 'Sr No', width: 12 },
            { label: 'Item Code', width: 20 },
            { label: 'Description', width: 50 },
            { label: 'HSN/SAC', width: 20 },
            { label: 'Qty', width: 13 },
            { label: 'Rate', width: 20 },
            { label: 'Unit', width: 12 },
            { label: 'Amount', width: 43 },
          ];
        } else {
          columns = [
            { label: 'Sr No', width: 12 },
            { label: 'Item Code', width: 22 },
            { label: 'Description', width: 68 },
            { label: 'Qty', width: 14 },
            { label: 'Rate', width: 20 },
            { label: 'Unit', width: 14 },
            { label: 'Amount', width: 40 },
          ];
        }

        pdf.setDrawColor(203, 213, 225);
        let x = marginX;
        columns.forEach((col) => {
          pdf.rect(x, y, col.width, 8, 'S');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(8);
          pdf.setTextColor(0, 0, 0);
          pdf.text(col.label, x + col.width / 2, y + 5.1, { align: 'center' });
          x += col.width;
        });
        return columns;
      };

      const drawRow = (y, row, srNo, columns) => {
        const isOnlyTaxInvoice = documentTitle === 'TAX INVOICE' || numberField === 'taxInvoiceNo';
        let descIndex;
        let hsnIndex = -1;
        let values;

        if (isOnlyChalan) {
          descIndex = 2;
          if (isExtendedDetails) {
            hsnIndex = 3;
            values = [
              String(srNo),
              row.itemCode || '',
              pdf.splitTextToSize(row.description || '', columns[descIndex].width - 4),
              pdf.splitTextToSize(row.hsnSac || '', columns[hsnIndex].width - 4),
              row.quantity || '',
            ];
          } else {
            values = [
              String(srNo),
              row.itemCode || '',
              pdf.splitTextToSize(row.description || '', columns[descIndex].width - 4),
              row.quantity || '',
            ];
          }
        } else if (isOnlyTaxInvoice) {
          descIndex = 2;
          hsnIndex = 3;
          const amount = parseNumber(row.quantity) * parseNumber(row.rate);
          values = [
            String(srNo),
            row.itemCode || '',
            pdf.splitTextToSize(row.description || '', columns[descIndex].width - 4),
            pdf.splitTextToSize(row.hsnSac || '', columns[hsnIndex].width - 4),
            row.quantity || '',
            formatMoney(parseNumber(row.rate)),
            row.unit || '',
            formatMoney(amount),
          ];
        } else {
          descIndex = 2;
          const amount = parseNumber(row.quantity) * parseNumber(row.rate);
          values = [
            String(srNo),
            row.itemCode || '',
            pdf.splitTextToSize(row.description || '', columns[descIndex].width - 4),
            row.quantity || '',
            formatMoney(parseNumber(row.rate)),
            row.unit || '',
            formatMoney(amount),
          ];
        }

        const descLines = values[descIndex];
        const hsnLines = hsnIndex >= 0 ? values[hsnIndex] : [];
        const lineCount = Math.max(1, Array.isArray(descLines) ? descLines.length : 0, Array.isArray(hsnLines) ? hsnLines.length : 0);
        const rowH = Math.max(8, lineCount * 4.6 + 2);

        let x = marginX;
        pdf.setDrawColor(203, 213, 225);
        pdf.setTextColor(dark);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.6);

        columns.forEach((col, idx) => {
          pdf.rect(x, y, col.width, rowH);
          const textX = x + (idx === 0 ? col.width / 2 : (idx === columns.length - 1 && !isOnlyChalan) ? col.width - 2 : 2);
          const textOpts = idx === 0 || (idx === columns.length - 1 && !isOnlyChalan) ? { align: idx === 0 ? 'center' : 'right' } : {};

          if (idx === descIndex || idx === hsnIndex) {
            pdf.text(values[idx], x + 2, y + 5, { maxWidth: col.width - 4 });
          } else {
            pdf.text(values[idx], textX, y + 5, textOpts);
          }
          x += col.width;
        });

        return rowH;
      };

      drawHeader();
      drawPartySection(48);

      const columns = drawTableHeader(48 + partySectionH + 4);
      let y = 48 + partySectionH + 12;
      rows.forEach((row, index) => {
        const rowHeight = drawRow(y, row, index + 1, columns);
        y += rowHeight;
      });

      const summaryY = Math.max(y + 6, 170);
      let summaryBoxH = 60;
      // Calculate summary box height dynamically
      const calculateSummaryBoxHeight = () => {
        if (isOnlyChalan) return 0;
        const headerHeight = 8;
        const topPadding = 12;
        const bottomPadding = 12; // for amount in words
        const chargeLineHeight = 8;
        const summaryLineHeight = 8;
        const totalLineExtra = 2;
        
        const isOnlyTaxInvoice = documentTitle === 'TAX INVOICE' || numberField === 'taxInvoiceNo';
        if (isOnlyTaxInvoice) {
          const chargeLinesCount = 4;
          const summaryLinesCount = 3;
          const maxContentHeight = Math.max(chargeLinesCount * chargeLineHeight, (summaryLinesCount - 1) * summaryLineHeight + (summaryLineHeight + totalLineExtra));
          return headerHeight + topPadding + maxContentHeight + bottomPadding;
        } else {
          const termLines = [
            `${taxLabel}: ${terms.tax || '0'}%`,
            `Payment: ${terms.payment || 'As per mutual agreement'}`,
            `Delivery: ${terms.deliverySchedule || 'As discussed'}`,
            `Packing & Forwarding: ${terms.packingForwardingPercent || '0'}%`,
            ...(showFreightAmountField || includeFreightInTotal ? [`Freight Amount: ${terms.freightAmount ? formatMoney(parseNumber(terms.freightAmount)) : '0.00'}`] : []),
            `Validity: ${terms.validity || 'As per offer'}`,
          ];
          const termHeight = termLines.length * 4.1;
          const summaryLinesCount = 3 + (includePackingForwardingInTotal ? 1 : 0);
          const summaryHeight = (summaryLinesCount - 1) * summaryLineHeight + (summaryLineHeight + totalLineExtra);
          const maxContentHeight = Math.max(termHeight, summaryHeight);
          return headerHeight + topPadding + maxContentHeight + bottomPadding;
        }
      };
      
      summaryBoxH = calculateSummaryBoxHeight();
      
      if (!isOnlyChalan) {
        const isOnlyTaxInvoice = documentTitle === 'TAX INVOICE' || numberField === 'taxInvoiceNo';
        if (isOnlyTaxInvoice) {
          const leftW = 122;
          const rightX = marginX + leftW + 4;
          const rightW = contentW - leftW - 4;

          pdf.setDrawColor(203, 213, 225);
          pdf.roundedRect(marginX, summaryY, leftW, summaryBoxH, 2, 2, 'S');
          pdf.roundedRect(rightX, summaryY, rightW, summaryBoxH, 2, 2, 'S');

          pdf.setFillColor(light);
          pdf.rect(marginX, summaryY, leftW, 8, 'F');
          pdf.rect(rightX, summaryY, rightW, 8, 'F');

          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(dark);
          pdf.text('CHARGES', marginX + 3, summaryY + 5.5);
          pdf.text('TOTAL SUMMARY', rightX + 3, summaryY + 5.5);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.2);
          pdf.setTextColor(muted);
          const chargeLines = [
            { label: 'Packing & Forwarding Charge', value: formatMoney(taxPackingForwardingCharge) },
            { label: 'Freight Charge', value: formatMoney(taxFreightCharge) },
            { label: 'Discount', value: `${taxDiscountPercent || 0}% (- ${formatMoney(taxDiscountAmount)})` },
            { label: 'Main Price', value: formatMoney(mainPrice) },
          ];
          let chargeY = summaryY + 12;
          chargeLines.forEach((line) => {
            pdf.text(line.label, marginX + 3, chargeY);
            pdf.text(line.value, marginX + leftW - 3, chargeY, { align: 'right' });
            chargeY += 8;
          });

          const summaryLines = [
            { label: 'Main Price', value: formatMoney(mainPrice) },
            { label: `${taxLabel} ${terms.tax || 0}%`, value: formatMoney(taxAmount) },
            { label: totalLabel, value: formatMoney(grandTotal) },
          ];
          let sumY = summaryY + 14;
          summaryLines.forEach((line) => {
            const isTotalRow = line.label === totalLabel;
            pdf.setTextColor(isTotalRow ? dark : muted);
            pdf.setFont('helvetica', isTotalRow ? 'bold' : 'normal');
            pdf.text(line.label, rightX + 3, sumY);
            pdf.text(line.value, rightX + rightW - 3, sumY, { align: 'right' });
            sumY += isTotalRow ? 10 : 8;
          });
          // Add amount in words
          const amountInWords = numberToWords(grandTotal);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9);
          pdf.setTextColor(dark);
          pdf.text('Amount in Words:', marginX + 3, summaryY + summaryBoxH - 10);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.5);
          drawWrappedText(pdf, amountInWords, marginX + 3, summaryY + summaryBoxH - 4, contentW - 6, 4.2);
        } else if (isExtendedDetails) { // CHALAN? Wait no, isOnlyChalan is excluded
          const leftW = 122;
          const rightX = marginX + leftW + 4;
          const rightW = contentW - leftW - 4;

          pdf.setDrawColor(203, 213, 225);
          pdf.roundedRect(marginX, summaryY, leftW, summaryBoxH, 2, 2, 'S');
          pdf.roundedRect(rightX, summaryY, rightW, summaryBoxH, 2, 2, 'S');

          pdf.setFillColor(light);
          pdf.rect(marginX, summaryY, leftW, 8, 'F');
          pdf.rect(rightX, summaryY, rightW, 8, 'F');

          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(dark);
          pdf.text('TERMS & CONDITIONS', marginX + 3, summaryY + 5.5);
          pdf.text('TOTAL SUMMARY', rightX + 3, summaryY + 5.5);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.2);
          pdf.setTextColor(muted);
          const termLines = [
            `${taxLabel}: ${terms.tax || '0'}%`,
            `Payment: ${terms.payment || 'As per mutual agreement'}`,
            `Delivery: ${terms.deliverySchedule || 'As discussed'}`,
            `Packing & Forwarding: ${terms.packingForwardingPercent || '0'}%`,
            ...(showFreightAmountField || includeFreightInTotal ? [`Freight Amount: ${terms.freightAmount ? formatMoney(parseNumber(terms.freightAmount)) : '0.00'}`] : []),
            `Validity: ${terms.validity || 'As per offer'}`,
          ];
          let termY = summaryY + 12;
          termLines.forEach((line) => {
            termY += drawWrappedText(pdf, line, marginX + 3, termY, leftW - 6, 4.1);
          });

          const summaryLines = [
            { label: 'Subtotal', value: formatMoney(subtotal) },
            ...(includePackingForwardingInTotal ? [{ label: 'Packing & Forwarding Amount', value: formatMoney(packingForwardingAmount) }] : []),
            { label: `${taxLabel} ${terms.tax || 0}%`, value: formatMoney(taxAmount) },
            { label: totalLabel, value: formatMoney(grandTotal) },
          ];
          let sumY = summaryY + 14;
          summaryLines.forEach((line) => {
            const isTotalRow = line.label === totalLabel;
            pdf.setTextColor(isTotalRow ? dark : muted);
            pdf.setFont('helvetica', isTotalRow ? 'bold' : 'normal');
            pdf.text(line.label, rightX + 3, sumY);
            pdf.text(line.value, rightX + rightW - 3, sumY, { align: 'right' });
            sumY += isTotalRow ? 10 : 8;
          });
          // Add amount in words
          const amountInWords = numberToWords(grandTotal);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9);
          pdf.setTextColor(dark);
          pdf.text('Amount in Words:', marginX + 3, summaryY + summaryBoxH - 10);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.5);
          drawWrappedText(pdf, amountInWords, marginX + 3, summaryY + summaryBoxH - 4, contentW - 6, 4.2);
        } else {
          const leftW = 122;
          const rightX = marginX + leftW + 4;
          const rightW = contentW - leftW - 4;

          pdf.setDrawColor(203, 213, 225);
          pdf.roundedRect(marginX, summaryY, leftW, summaryBoxH, 2, 2, 'S');
          pdf.roundedRect(rightX, summaryY, rightW, summaryBoxH, 2, 2, 'S');

          pdf.setFillColor(light);
          pdf.rect(marginX, summaryY, leftW, 8, 'F');
          pdf.rect(rightX, summaryY, rightW, 8, 'F');

          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(dark);
          pdf.text('TERMS & CONDITIONS', marginX + 3, summaryY + 5.5);
          pdf.text('TOTAL SUMMARY', rightX + 3, summaryY + 5.5);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.2);
          pdf.setTextColor(muted);
          const termLines = [
            `${taxLabel}: ${terms.tax || '0'}%`,
            `Payment: ${terms.payment || 'As per mutual agreement'}`,
            `Delivery: ${terms.deliverySchedule || 'As discussed'}`,
            `Packing & Forwarding: ${terms.packingForwardingPercent || '0'}%`,
            ...(showFreightAmountField || includeFreightInTotal ? [`Freight Amount: ${terms.freightAmount ? formatMoney(parseNumber(terms.freightAmount)) : '0.00'}`] : []),
            `Validity: ${terms.validity || 'As per offer'}`,
          ];
          let termY = summaryY + 12;
          termLines.forEach((line) => {
            termY += drawWrappedText(pdf, line, marginX + 3, termY, leftW - 6, 4.1);
          });

          const summaryLines = [
            { label: 'Subtotal', value: formatMoney(subtotal) },
            ...(includePackingForwardingInTotal ? [{ label: 'Packing & Forwarding Amount', value: formatMoney(packingForwardingAmount) }] : []),
            { label: `${taxLabel} ${terms.tax || 0}%`, value: formatMoney(taxAmount) },
            { label: totalLabel, value: formatMoney(grandTotal) },
          ];
          let sumY = summaryY + 14;
          summaryLines.forEach((line) => {
            const isTotalRow = line.label === totalLabel;
            pdf.setTextColor(isTotalRow ? dark : muted);
            pdf.setFont('helvetica', isTotalRow ? 'bold' : 'normal');
            pdf.text(line.label, rightX + 3, sumY);
            pdf.text(line.value, rightX + rightW - 3, sumY, { align: 'right' });
            sumY += isTotalRow ? 10 : 8;
          });
          // Add amount in words
          const amountInWords = numberToWords(grandTotal);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9);
          pdf.setTextColor(dark);
          pdf.text('Amount in Words:', marginX + 3, summaryY + summaryBoxH - 10);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.5);
          drawWrappedText(pdf, amountInWords, marginX + 3, summaryY + summaryBoxH - 4, contentW - 6, 4.2);
        }
      }

      if (isOnlyChalan) {
        // For Challan, show Total Quantity section
        const totalQtyY = summaryY + 4;
        pdf.setDrawColor(203, 213, 225);
        pdf.roundedRect(marginX, totalQtyY, contentW, 30, 2, 2, 'S');
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(marginX, totalQtyY, contentW, 8, 2, 2, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9.5);
        pdf.setTextColor(dark);
        pdf.text('TOTAL QUANTITY', marginX + 3, totalQtyY + 5.5);
        const totalQty = rows.reduce((sum, row) => sum + (parseNumber(row.quantity) || 0), 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(`Grand Total: ${totalQty}`, marginX + 3, totalQtyY + 20);
      } else {
        const notesText = terms.notes?.trim();
        if (notesText) {
          const notesY = summaryY + summaryBoxH + 4;
          const notesH = 18;
          pdf.setDrawColor(203, 213, 225);
          pdf.roundedRect(marginX, notesY, contentW, notesH, 2, 2, 'S');
          pdf.setFillColor(248, 250, 252);
          pdf.roundedRect(marginX, notesY, contentW, 6, 2, 2, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9.5);
          pdf.setTextColor(dark);
          pdf.text('NOTES', marginX + 3, notesY + 4.2);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.2);
          pdf.setTextColor(muted);
          pdf.text(notesText, marginX + 3, notesY + 10, { maxWidth: contentW - 6 });
        }
      }

      let notesBottom = summaryY + summaryBoxH;
      if (isOnlyChalan) {
        notesBottom = summaryY + 4 + 30;
      } else if (terms.notes?.trim()) {
        notesBottom = summaryY + summaryBoxH + 4 + 18;
      }
      const footerLineY = Math.max(notesBottom + 20, 275);
      pdf.setDrawColor(148, 163, 184);
      pdf.line(marginX, footerLineY, pageW - marginX, footerLineY);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(muted);
      pdf.text(`For ${issuer.companyName || 'KUSCO PVT LTD'}`, marginX, footerLineY + 7);
      pdf.text('Authorized Signatory', pageW - marginX, footerLineY + 7, { align: 'right' });
      pdf.text(
        footerNote || 'This document is computer generated and does not require a signature.',
        marginX,
        footerLineY + 14,
        { maxWidth: contentW - 70 }
      );

      return {
        pdf,
        filename: `${filePrefix}-${documentMeta[numberField] || 'draft'}.pdf`,
      };
    } catch {
      // Silent failure keeps the editor usable if rendering hiccups.
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const generatePDF = async () => {
    const result = await buildPdfDocument();
    if (!result) return;
    result.pdf.save(result.filename);
  };

  const shareDocument = async (e) => {
    e.preventDefault();
    setShareError('');
    setShareSuccess('');

    if (!inq?._id) {
      setShareError('Please open this PDF from an inquiry before sharing it.');
      return;
    }
    if (!shareDept) {
      setShareError('Please select a department.');
      return;
    }

    setShareLoading(true);
    try {
      const result = await buildPdfDocument();
      if (!result) return;

      const blob = result.pdf.output('blob');
      const file = new File([blob], result.filename, { type: 'application/pdf' });
      const fd = new FormData();
      fd.append('department', shareDept);
      const documentTypeMap = {
        quotationNo: 'quotation',
        invoiceNo: 'proforma',
        taxInvoiceNo: 'tax-invoice',
        chalanNo: 'chalan',
      };
      fd.append('documentType', documentTypeMap[numberField] || 'document');
      fd.append('comment', shareComment);
      fd.append('attachment', file);

      await customerAPI.shareDocument(inq._id, fd);
      setShareDept('');
      setShareComment('');
      setShareOpen(false);
      setShareSuccess('PDF shared successfully.');
    } catch (err) {
      setShareError(err.response?.data?.message || 'Failed to share PDF.');
    } finally {
      setShareLoading(false);
    }
  };

  return (
    <div className="doc-page">
      <div className="doc-topbar">
        <button
          type="button"
          className="btn-back"
          onClick={() => navigate(`${ROUTES.ADMIN_DEPARTMENT}/Sales%20Coordinator`)}
        >
          <MdArrowBack /> Back
        </button>
        <h2 className="doc-title" style={{ color: titleBarColor || '#111827' }}>{pageTitle}</h2>
      </div>

      <div className="doc-preview">
        <div className="doc-header-strip" style={{ borderColor: accentColor || '#667eea' }}>
          <div className="doc-brand-block">
            <img src={logo} alt="Company logo" className="doc-logo" />
            <div className="doc-brand-copy">
              <input
                name="companyName"
                value={issuer.companyName}
                onChange={handleIssuerChange}
                className="doc-company-name-input"
                placeholder="Company name"
              />
              <input
                name="address"
                value={issuer.address}
                onChange={handleIssuerChange}
                className="doc-company-line-input"
                placeholder="Company address"
              />
              <div className="doc-company-mini-grid">
                <input
                  name="gstNo"
                  value={issuer.gstNo}
                  onChange={handleIssuerChange}
                  placeholder="GST No"
                />
                <input
                  name="panNo"
                  value={issuer.panNo}
                  onChange={handleIssuerChange}
                  placeholder="PAN No"
                />
                <input
                  name="hsnCode"
                  value={issuer.hsnCode}
                  onChange={handleIssuerChange}
                  placeholder="HSN Code"
                />
                <input
                  name="msmeNo"
                  value={issuer.msmeNo}
                  onChange={handleIssuerChange}
                  placeholder="MSME No"
                />
                <input
                  name="phone"
                  value={issuer.phone}
                  onChange={handleIssuerChange}
                  placeholder="Phone"
                />
                <input
                  name="email"
                  value={issuer.email}
                  onChange={handleIssuerChange}
                  placeholder="Email"
                />
                <input
                  name="state"
                  value={issuer.state}
                  onChange={handleIssuerChange}
                  placeholder="State"
                />
                <input
                  name="stateCode"
                  value={issuer.stateCode}
                  onChange={handleIssuerChange}
                  placeholder="State Code"
                />
                <input
                  name="website"
                  value={issuer.website}
                  onChange={handleIssuerChange}
                  placeholder="Website"
                />
              </div>
            </div>
          </div>

          <div className="doc-meta-card">
            <div className="doc-meta-title" style={{ background: accentColor || '#667eea' }}>
              {documentTitle}
            </div>
            <div className="doc-meta-grid">
              <label>
                <span>{numberLabel}</span>
                <input
                  name={numberField}
                  value={documentNumber || ''}
                  readOnly
                  placeholder={numberPlaceholder}
                  className="doc-number-readonly"
                />
              </label>
              <label>
                <span>Document Date</span>
                <input
                  name="documentDate"
                  type="date"
                  value={documentMeta.documentDate}
                  onChange={handleMetaChange}
                />
              </label>
              <label>
                <span>Inquiry No</span>
                <input
                  name="inquiryNo"
                  value={documentMeta.inquiryNo || ''}
                  readOnly
                  placeholder="Inquiry number"
                  className="doc-number-readonly"
                />
              </label>
              <label>
                <span>Inquiry Date</span>
                <input
                  name="inquiryDate"
                  type="date"
                  value={documentMeta.inquiryDate}
                  onChange={handleMetaChange}
                />
              </label>
              <label className="doc-meta-full">
                <span>Inquiry Due Date</span>
                <input
                  name="inquiryDueDate"
                  type="date"
                  value={documentMeta.inquiryDueDate}
                  onChange={handleMetaChange}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="doc-two-col">
          <section className="doc-section">
            <div className="doc-section-heading">Customer Details</div>
            <div className="doc-form-grid">
              <label>
                <span>Company Name</span>
                <input
                  name="customerName"
                  value={documentMeta.customerName}
                  onChange={handleMetaChange}
                  placeholder="Customer company"
                />
              </label>
              <label>
                <span>Kind Attention</span>
                <input
                  name="customerAttention"
                  value={documentMeta.customerAttention}
                  onChange={handleMetaChange}
                  placeholder="Contact person"
                />
              </label>
              <label>
                <span>Address</span>
                <input
                  name="customerAddress"
                  value={documentMeta.customerAddress}
                  onChange={handleMetaChange}
                  placeholder="Customer address"
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  name="customerEmail"
                  value={documentMeta.customerEmail}
                  onChange={handleMetaChange}
                  placeholder="Customer email"
                />
              </label>
              <label>
                <span>Phone</span>
                <input
                  name="customerPhone"
                  value={documentMeta.customerPhone}
                  onChange={handleMetaChange}
                  placeholder="Customer phone"
                />
              </label>
              <label>
                <span>GST No</span>
                <input
                  name="customerGstNo"
                  value={documentMeta.customerGstNo}
                  onChange={handleMetaChange}
                  placeholder="Customer GST"
                />
              </label>
            </div>
          </section>

          <section className="doc-section">
            <div className="doc-section-heading">{isExtendedDetails ? 'PO & Dispatch Details' : 'Reference Summary'}</div>
            {isExtendedDetails ? (
              <div className="doc-form-grid">
                <label>
                  <span>PO Number</span>
                  <input
                    name="poNumber"
                    value={documentMeta.poNumber}
                    onChange={handleMetaChange}
                    placeholder="PO Number"
                  />
                </label>
                <label>
                  <span>PO Date</span>
                  <input
                    name="poDate"
                    type="date"
                    value={documentMeta.poDate}
                    onChange={handleMetaChange}
                  />
                </label>
                {!isOnlyChalan && (
                  <label>
                    <span>Challan Number</span>
                    <input
                      name="challanNumber"
                      value={documentMeta.challanNumber}
                      onChange={handleMetaChange}
                      placeholder="Challan Number"
                    />
                  </label>
                )}
                {!isOnlyChalan && (
                  <label>
                    <span>Challan Date</span>
                    <input
                      name="challanDate"
                      type="date"
                      value={documentMeta.challanDate}
                      onChange={handleMetaChange}
                    />
                  </label>
                )}
                <label>
                  <span>Destination</span>
                  <input
                    name="destination"
                    value={documentMeta.destination}
                    onChange={handleMetaChange}
                    placeholder="Destination"
                  />
                </label>
                <label>
                  <span>Dispatch Through</span>
                  <input
                    name="dispatchThrough"
                    value={documentMeta.dispatchThrough}
                    onChange={handleMetaChange}
                    placeholder="Dispatch Through"
                  />
                </label>
              </div>
            ) : (
              <div className="doc-summary-cards">
                <div className="doc-summary-card">
                  <span>Inquiry</span>
                  <strong>{documentMeta.inquiryNo || 'Draft'}</strong>
                </div>
                <div className="doc-summary-card">
                  <span>Document</span>
                  <strong>{documentMeta[numberField] || 'Draft'}</strong>
                </div>
                <div className="doc-summary-card">
                  <span>Customer</span>
                  <strong>{documentMeta.customerName || 'Not set'}</strong>
                </div>
                <div className="doc-summary-card">
                  <span>Prepared For</span>
                  <strong>{documentMeta.customerAttention || 'Not set'}</strong>
                </div>
              </div>
            )}
          </section>
        </div>

        <section className="doc-section">
          <div className="doc-section-heading">Items</div>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>Item Code</th>
                <th>Description</th>
                {isExtendedDetails && <th>HSN/SAC</th>}
                <th>Qty</th>
                {!isOnlyChalan && <th>Rate</th>}
                {!isOnlyChalan && <th>Unit</th>}
                {!isOnlyChalan && <th>Amount</th>}
                <th className="doc-table-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const amount = parseNumber(row.quantity) * parseNumber(row.rate);

                return (
                  <tr key={index}>
                    <td className="doc-sr">{index + 1}</td>
                    <td>
                      <input
                        name="itemCode"
                        value={row.itemCode}
                        onChange={(e) => handleRowChange(index, e)}
                        placeholder="Code"
                      />
                    </td>
                    <td>
                      <input
                        name="description"
                        value={row.description}
                        onChange={(e) => handleRowChange(index, e)}
                        placeholder="Description"
                      />
                    </td>
                    {isExtendedDetails && (
                      <td>
                        <input
                          name="hsnSac"
                          value={row.hsnSac}
                          onChange={(e) => handleRowChange(index, e)}
                          placeholder="HSN/SAC"
                        />
                      </td>
                    )}
                    <td>
                      <input
                        name="quantity"
                        type="number"
                        min="0"
                        step="any"
                        value={row.quantity}
                        onChange={(e) => handleRowChange(index, e)}
                        placeholder="0"
                      />
                    </td>
                    {!isOnlyChalan && (
                      <td>
                        <input
                          name="rate"
                          type="number"
                          min="0"
                          step="any"
                          value={row.rate}
                          onChange={(e) => handleRowChange(index, e)}
                          placeholder="0.00"
                        />
                      </td>
                    )}
                    {!isOnlyChalan && (
                      <td>
                        <input
                          name="unit"
                          value={row.unit}
                          onChange={(e) => handleRowChange(index, e)}
                          placeholder="Unit"
                        />
                      </td>
                    )}
                    {!isOnlyChalan && <td className="doc-amount">{formatMoney(amount)}</td>}
                    <td className="doc-table-action">
                      {rows.length > 1 && (
                        <button type="button" className="doc-row-del" onClick={() => removeRow(index)}>
                          <MdDelete />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <button type="button" className="doc-add-row" onClick={addRow}>
            <MdAdd /> Add Row
          </button>
        </section>

        {!isOnlyChalan && (
        <div className="doc-bottom-grid">
          <section className="doc-section doc-summary-section">
            <div className="doc-section-heading">Totals</div>
            <div className="doc-total-line">
              <span>Subtotal</span>
              <strong>{formatMoney(subtotal)}</strong>
            </div>
            {!moveTaxBelowPacking && (
              <div className="doc-total-inline">
                <label>
                  <span>{taxLabel} %</span>
                  <input
                    name="tax"
                    type="number"
                    min="0"
                    step="any"
                    value={terms.tax}
                    onChange={handleTermsChange}
                    placeholder="0"
                  />
                </label>
              </div>
            )}
            {includeFreightInTotal && (
              <div className="doc-total-line">
                <span>Freight Amount</span>
                <strong>{formatMoney(parseNumber(terms.freightAmount))}</strong>
              </div>
            )}
            {includePackingForwardingInTotal && (
              <div className="doc-total-inline">
                <label>
                  <span>Packing &amp; Forwarding %</span>
                  <input
                    name="packingForwardingPercent"
                    type="number"
                    min="0"
                    step="any"
                    value={terms.packingForwardingPercent || ''}
                    onChange={handleTermsChange}
                    placeholder="0"
                  />
                </label>
              </div>
            )}
            {includePackingForwardingInTotal && (
              <div className="doc-total-line">
                <span>Packing &amp; Forwarding Amount</span>
                <strong>{formatMoney(packingForwardingAmount)}</strong>
              </div>
            )}
            {moveTaxBelowPacking && (
              <div className="doc-total-inline">
                <label>
                  <span>{taxLabel} %</span>
                  <input
                    name="tax"
                    type="number"
                    min="0"
                    step="any"
                    value={terms.tax}
                    onChange={handleTermsChange}
                    placeholder="0"
                  />
                </label>
              </div>
            )}
            {moveTaxBelowPacking && (
              <div className="doc-total-line">
                <span>{taxLabel} Amount</span>
                <strong>{formatMoney(taxAmount)}</strong>
              </div>
            )}
            {showTaxAmountLine && (
              <div className="doc-total-line">
                <span>Tax Amount</span>
                <strong>{formatMoney(taxAmount)}</strong>
              </div>
            )}
            <div className="doc-total-line doc-total-line--grand">
              <span>{totalLabel}</span>
              <strong>{formatMoney(grandTotal)}</strong>
            </div>
          </section>

          {isExtendedDetails ? (
            <section className="doc-section">
                <div className="doc-section-heading">Charges</div>
                <div className="doc-form-grid doc-form-grid--terms">
                  <label>
                    <span>Packing &amp; Forwarding Charge</span>
                    <input
                      name="packingForwardingAmount"
                      type="number"
                      min="0"
                      step="any"
                      value={terms.packingForwardingAmount || ''}
                      onChange={handleTermsChange}
                      placeholder="0.00"
                    />
                  </label>
                  <label>
                    <span>Freight Charge</span>
                    <input
                      name="freightCharge"
                      type="number"
                      min="0"
                      step="any"
                      value={terms.freightCharge || ''}
                      onChange={handleTermsChange}
                      placeholder="0.00"
                    />
                  </label>
                  <label>
                    <span>Discount %</span>
                    <input
                      name="discountPercent"
                      type="number"
                      min="0"
                      step="any"
                      value={terms.discountPercent || ''}
                      onChange={handleTermsChange}
                      placeholder="0"
                    />
                  </label>
                  <label>
                    <span>GST %</span>
                    <input
                      name="tax"
                      type="number"
                      min="0"
                      step="any"
                      value={terms.tax}
                      onChange={handleTermsChange}
                      placeholder="0"
                    />
                  </label>
                </div>
            </section>
          ) : (
            <section className="doc-section">
              <div className="doc-section-heading">Terms and Conditions</div>
              <div className="doc-form-grid doc-form-grid--terms">
                <label>
                  <span>Payment</span>
                  <input
                    name="payment"
                    value={terms.payment}
                    onChange={handleTermsChange}
                    placeholder="Payment terms"
                  />
                </label>
                <label>
                  <span>Delivery Schedule</span>
                  <input
                    name="deliverySchedule"
                    value={terms.deliverySchedule}
                    onChange={handleTermsChange}
                    placeholder="Delivery schedule"
                  />
                </label>
                <label>
                  <span>Packing &amp; Forwarding %</span>
                  <input
                    name="packingForwardingPercent"
                    type="number"
                    min="0"
                    step="any"
                    value={terms.packingForwardingPercent || ''}
                    onChange={handleTermsChange}
                    placeholder="0"
                  />
                </label>
                {(showFreightAmountField || includeFreightInTotal) && (
                  <label>
                    <span>Freight Amount</span>
                    <input
                      name="freightAmount"
                      type="number"
                      min="0"
                      step="any"
                      value={terms.freightAmount || ''}
                      onChange={handleTermsChange}
                      placeholder="Freight amount"
                    />
                  </label>
                )}
                <label>
                  <span>Validity</span>
                  <input
                    name="validity"
                    value={terms.validity}
                    onChange={handleTermsChange}
                    placeholder="Offer validity"
                  />
                </label>
                <label className="doc-meta-full">
                  <span>Notes</span>
                  <textarea
                    name="notes"
                    value={terms.notes}
                    onChange={handleTermsChange}
                    placeholder="Additional notes"
                    rows={3}
                  />
                </label>
              </div>
            </section>
          )}
        </div>
        )}

        {isOnlyChalan && (
          <section className="doc-section doc-summary-section">
            <div className="doc-section-heading">Total Quantity</div>
            <div className="doc-total-line doc-total-line--grand">
              <span>Grand Total</span>
              <strong>{rows.reduce((sum, row) => sum + (parseNumber(row.quantity) || 0), 0)}</strong>
            </div>
          </section>
        )}

        <div className="doc-footer">
          <div className="doc-footer-sign">
            <span>Authorized Signatory</span>
            <strong>For {issuer.companyName}</strong>
          </div>
          <p>{footerNote || 'This document was generated electronically and is valid without a signature.'}</p>
        </div>
      </div>

      <div className="doc-bottom-bar">
        <button
          type="button"
          className="doc-pdf-btn"
          onClick={generatePDF}
          disabled={generating}
          style={{
            background: pdfButtonColor || 'linear-gradient(135deg, #667eea, #764ba2)',
          }}
        >
          <MdPictureAsPdf /> {generating ? 'Generating...' : 'Download PDF'}
        </button>
        <button
          type="button"
          className="doc-pdf-btn doc-pdf-btn--share"
          onClick={() => setShareOpen((prev) => !prev)}
          disabled={generating}
        >
          <MdShare /> Share PDF
        </button>
      </div>
      {(shareSuccess || shareError) && !shareOpen && (
        <div className="doc-share-status" role="status" aria-live="polite">
          {shareError && <p className="doc-share-error">{shareError}</p>}
          {shareSuccess && <p className="doc-share-success">{shareSuccess}</p>}
        </div>
      )}
      {shareOpen && (
        <form className="doc-share-panel" onSubmit={shareDocument}>
          <div className="doc-share-panel-head">
            <strong>Share PDF with Department</strong>
            <button
              type="button"
              className="doc-share-close"
              onClick={() => setShareOpen(false)}
            >
              <MdClose />
            </button>
          </div>
          {shareError && <p className="doc-share-error">{shareError}</p>}
          {shareSuccess && <p className="doc-share-success">{shareSuccess}</p>}
          <div className="doc-share-grid">
            <label>
              <span>Department</span>
              <select value={shareDept} onChange={(e) => setShareDept(e.target.value)}>
                <option value="">-- Select department --</option>
                {DEPARTMENTS.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </label>
            <label className="doc-share-full">
              <span>Comment</span>
              <textarea
                value={shareComment}
                onChange={(e) => setShareComment(e.target.value)}
                placeholder="Optional note"
                rows={3}
              />
            </label>
          </div>
          <div className="doc-share-actions">
            <button
              type="button"
              className="doc-share-cancel"
              onClick={() => setShareOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="doc-share-send"
              disabled={shareLoading || generating || !shareDept}
            >
              {shareLoading ? 'Sharing...' : 'Send PDF'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DocumentComposer;
